import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PhoneCall, Plus, Search, Trash2, Edit3, ExternalLink, Mail, Printer, User, Globe } from "lucide-react";

const CATEGORIES = ["vendor", "internal", "external", "other"];
const empty = { company_name: "", category: "vendor", contact_name: "",
                phone: "", email: "", fax: "", website: "", note: "" };

export default function Contacts() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState(null); // null | {}
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (cat) qs.set("category", cat);
    api.get(`/contacts?${qs.toString()}`)
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Failed to load contacts"))
      .finally(() => setLoading(false));
  }, [q, cat]);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    try {
      if (row.id) await api.put(`/contacts/${row.id}`, row);
      else await api.post("/contacts", row);
      toast.success(row.id ? "Contact updated" : "Contact added");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this contact?")) return;
    try { await api.delete(`/contacts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800" data-testid="contacts-header">
            Phonebook &amp; Contacts
          </h1>
          <p className="text-sm text-slate-500">Vendors, internal teams, and external partners — all searchable.</p>
        </div>
        <Button data-testid="contact-new-btn" onClick={() => setEditing({ ...empty })}
          className="bg-pink-700 hover:bg-pink-800 gap-1.5">
          <Plus className="w-4 h-4" /> New Contact
        </Button>
      </div>

      <Card className="p-3 border-slate-200 mb-4">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search company, contact, phone, email, website…"
              className="pl-8 h-9 text-sm" />
          </div>
          <select data-testid="contacts-cat" value={cat} onChange={(e) => setCat(e.target.value)}
            className="h-9 text-sm px-2 rounded-md border border-slate-200">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-x-auto">
        <Table data-testid="contacts-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Fax</TableHead>
              <TableHead>Website</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">
                {loading ? "Loading…" : "No contacts yet."}</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`contact-row-${r.id}`}>
                <TableCell>
                  <div className="font-medium text-slate-800">{r.company_name}</div>
                  <div className="text-[10px] uppercase text-slate-500 tracking-wide">{r.category}</div>
                </TableCell>
                <TableCell>{r.contact_name || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell>{r.phone ? <a href={`tel:${r.phone}`} className="text-pink-800 font-mono">{r.phone}</a> : <span className="text-slate-400">—</span>}</TableCell>
                <TableCell>{r.email ? <a href={`mailto:${r.email}`} className="text-pink-800">{r.email}</a> : <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="font-mono text-xs">{r.fax || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell>{r.website ? (
                  <a href={r.website.startsWith("http") ? r.website : `https://${r.website}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-pink-800 inline-flex items-center gap-1"><Globe className="w-3 h-3" /> visit</a>
                ) : <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => setEditing(r)} data-testid={`contact-edit-${r.id}`}
                    className="text-slate-500 hover:text-pink-800 mr-2"><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => del(r.id)} data-testid={`contact-del-${r.id}`}
                    className="text-slate-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {editing && <ContactDialog row={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ContactDialog({ row, onClose, onSave }) {
  const [f, setF] = useState(row);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{row.id ? "Edit Contact" : "Add Contact"}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Field label="Company name *" v={f.company_name} onChange={(v) => set("company_name", v)} testid="c-company" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Category</label>
              <select value={f.category} onChange={(e) => set("category", e.target.value)}
                data-testid="c-cat" className="w-full h-9 text-sm px-2 rounded-md border border-slate-200">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Point of contact" v={f.contact_name} onChange={(v) => set("contact_name", v)} testid="c-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Phone" v={f.phone} onChange={(v) => set("phone", v)} testid="c-phone" />
            <Field label="Fax" v={f.fax} onChange={(v) => set("fax", v)} testid="c-fax" />
          </div>
          <Field label="Email" v={f.email} onChange={(v) => set("email", v)} testid="c-email" />
          <Field label="Website URL" v={f.website} onChange={(v) => set("website", v)} testid="c-web" placeholder="https://…" />
          <Field label="Notes" v={f.note} onChange={(v) => set("note", v)} testid="c-note" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="c-save" onClick={() => onSave(f)}
            className="bg-pink-700 hover:bg-pink-800">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, v, onChange, testid, placeholder }) => (
  <div>
    <label className="text-[10px] text-slate-500 uppercase">{label}</label>
    <Input data-testid={testid} value={v || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} className="h-9 text-sm" />
  </div>
);
